import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft, Home, Loader2, CheckCircle2, XCircle,
  TrendingUp, TrendingDown, Minus, ClipboardList,
  Send, RefreshCw, History, Search, AlertTriangle,
  ChevronRight, ChevronDown, ChevronUp, ArrowUpDown,
  Filter,
} from "lucide-react";

// ---------------------------------------------------------------------------
// `useMediaQuery` hook — used to swap the dense desktop JDE grid for a
// mobile-native, card-based layout below 768 px. Desktop rendering stays
// completely untouched.
// ---------------------------------------------------------------------------
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("pr_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/** Global 401 handler — this page uses raw `axios` (not the shared
 *  `api` instance in `lib/api.js`), so we replicate the interceptor's
 *  behaviour locally: on a 401 we wipe the stale token and bounce the
 *  user to /login. Without this, an expired session shows "Missing
 *  token" toast forever with no way to recover. */
const handle401 = (e) => {
  if (e?.response?.status === 401
      || /Missing token|Invalid token/i.test(e?.response?.data?.detail || "")) {
    localStorage.removeItem("pr_token");
    localStorage.removeItem("pr_user");
    if (!window.location.pathname.includes("/login")) {
      window.location.href = "/login";
    }
    return true;
  }
  return false;
};

const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 4,
}));

const fmtUnit = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, {
  minimumFractionDigits: 2, maximumFractionDigits: 6,
}));

// "6.50 USD / 100 EA · 0.065/EA" when base qty ≠ 1, else "6.50 USD/EA".
function PriceText({ amt, bq, ccy, unit, testid }) {
  if (amt == null) return <span className="text-slate-400">—</span>;
  const b = Number(bq) || 1;
  const u = unit || "";
  return (
    <span className="whitespace-nowrap" data-testid={testid}>
      <span className="num font-semibold">{fmt(amt)}</span>
      <span className="text-[10px] text-slate-500 ml-0.5">
        {ccy || ""}{b !== 1 ? ` / ${fmtUnit(b).replace(/\.00$/, "")} ${u}` : (u ? `/${u}` : "")}
      </span>
      {b !== 1 && (
        <span className="block text-[10px] text-cyan-800 font-medium">
          = {fmtUnit(Number(amt) / b)}{u ? `/${u}` : ""}
        </span>
      )}
    </span>
  );
}

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString(undefined, {
    day: "2-digit", month: "short", year: "2-digit",
  }).replace(/ /g, "-");
};

/** Convert a raw `approval_history` stage code into a readable label.
 *  The `stage` field stores the state the draft was IN when the action
 *  happened (`pending_l1` = "at the L1-pending stage"), NOT the outcome.
 *  Rendering it verbatim reads as though L1 is still pending, so surface
 *  a level-based label ("L1", "L2", "Retry") and let the decision word
 *  carry the outcome ("approved" / "rejected"). */
const stageLabel = (s) => ({
  pending_l1:          "L1",
  pending_l2:          "L2",
  pending_approval:    "Approval",
  pending_sap_write:   "SAP write",
  sap_error:           "Retry",
  sap_written_in_prep: "Retry",
}[s] || s || "—");

// ---------------------------------------------------------------------------
// Shared sort utility — click a column header to cycle asc → desc → off.
// Every column extractor is a function so we can sort by nested paths
// (row.current_standing?.amount) without pre-flattening the row shape.
// ---------------------------------------------------------------------------
function _cmp(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  const as = String(a).toLowerCase();
  const bs = String(b).toLowerCase();
  return as < bs ? -1 : as > bs ? 1 : 0;
}
function useSort(defaultKey = null, defaultDir = "desc") {
  const [sort, setSort] = useState({ key: defaultKey, dir: defaultDir });
  const toggle = (key) => setSort((prev) => {
    if (prev.key !== key) return { key, dir: "asc" };
    if (prev.dir === "asc") return { key, dir: "desc" };
    return { key: null, dir: "desc" };  // third click clears sort
  });
  return [sort, toggle];
}
function applySort(rows, sort, extractors) {
  if (!sort?.key) return rows;
  const ext = extractors[sort.key];
  if (!ext) return rows;
  const factor = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => _cmp(ext(a), ext(b)) * factor);
}
// Sortable <th> — click to toggle direction; up/down/updown icon shows state.
function SortableTH({ label, sortKey, sort, onToggle, align = "left",
                       className = "", testid }) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ChevronUp : ChevronDown;
  const alignCls = align === "right" ? "text-right justify-end" : "text-left";
  return (
    <th className={`px-1.5 py-1 font-semibold ${alignCls} ${className}`}
        data-testid={testid}>
      <button type="button" onClick={() => onToggle(sortKey)}
              className={`inline-flex items-center gap-0.5 select-none cursor-pointer whitespace-nowrap ${active ? "text-teal-700" : "text-slate-600 hover:text-slate-900"}`}
              data-testid={testid ? `${testid}-btn` : undefined}>
        <span>{label}</span>
        <Icon size={10} className={active ? "opacity-100" : "opacity-40"} />
      </button>
    </th>
  );
}

/**
 * Standalone Approvals Inbox for List Price drafts.
 * Two views:
 *  - "Awaiting me"  — drafts where the current user is the approver.
 *  - "Submitted by me" — drafts the current user created that are still in-flight.
 *
 * Each awaiting-me row gets three context blocks (from the backend
 * `_lp_enrich_for_approval` helper): current standing price, lowest offer
 * across suppliers, and last PO price. These are what the approver needs
 * to say yes/no without navigating anywhere else.
 */
export default function ListPriceApprovals() {
  const nav = useNavigate();
  const loc = useLocation();
  // Route `/admin/list-price-history` opens directly on the History tab.
  const initialView = loc.pathname.includes("list-price-history") ? "history" : "awaiting_me";
  const [view, setView] = useState(initialView);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // draft_id currently being decided
  const [selected, setSelected] = useState(new Set());
  const [rejectFor, setRejectFor] = useState(null); // draft object being rejected
  const [rejectComment, setRejectComment] = useState("");
  const [bulkComment, setBulkComment] = useState("");
  const [bulkMode, setBulkMode] = useState(null); // "approve" | "reject" | null

  // History-tab-specific filters
  const [histScope, setHistScope] = useState("mine"); // "mine" | "all"
  const [histQ, setHistQ] = useState("");
  const [histFrom, setHistFrom] = useState("");
  const [histTo, setHistTo] = useState("");
  const [histTotal, setHistTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      if (view === "history") {
        const params = new URLSearchParams({ scope: histScope, limit: "200" });
        if (histQ.trim())    params.set("q", histQ.trim());
        if (histFrom)        params.set("date_from", histFrom);
        if (histTo)          params.set("date_to", histTo);
        const r = await axios.get(`${API}/list-prices/approvals/history?${params}`,
          { headers: authHeaders() });
        setRows(r.data.rows || []);
        setHistTotal(r.data.total || 0);
      } else {
        const r = await axios.get(`${API}/list-prices/approvals/queue?view=${view}&limit=100`,
          { headers: authHeaders() });
        // Belt-and-suspenders: drop any terminal-state rows client-side too,
        // so even if a stale backend build leaks a `sap_written` or
        // `rejected` row into the queue, the approver never sees a
        // "Written to SAP" card sitting under Awaiting my approval.
        const TERMINAL = new Set(["sap_written", "sap_written_released",
                                    "rejected", "cancelled"]);
        const rows = (r.data.rows || []).filter(
          (row) => view !== "awaiting_me" || !TERMINAL.has(row.status));
        setRows(rows);
      }
    } catch (e) {
      if (handle401(e)) return;
      toast.error(e.response?.data?.detail || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [view, histScope, histQ, histFrom, histTo]);

  useEffect(() => { load(); }, [load]);

  const decideOne = async (draft, decision, comment = null) => {
    setBusy(draft.draft_id);
    try {
      const r = await axios.post(
        `${API}/list-prices/drafts/${draft.draft_id}/decision`,
        { decision, comment },
        // Explicit 90 s timeout — the backend now caps the SAP SOAP
        // call at 45 s so a real success/failure always fits inside
        // this window. Anything longer here means the ingress dropped
        // the connection; the toast in `catch` handles that case.
        { headers: authHeaders(), timeout: 90000 });
      if (r.data.ok) {
        toast.success(decision === "approve"
          ? `Approved — ${r.data.new_status === "sap_written" ? "written to SAP" : r.data.new_status}`
          : "Rejected");
      } else {
        // Backend populates a plain-English `user_message` on every
        // failure path (Severity>=3 hint, HTTP error, transport
        // exception). Fall back through `message`, `sap_note`, `fault`
        // so we never surface a raw "unknown" to the approver.
        const msg = r.data.user_message
                     || r.data.message
                     || r.data.sap_note
                     || r.data.fault
                     || "See the draft audit log for the raw SAP response.";
        toast.error(msg, { duration: 15000, style: { maxWidth: 560 } });
      }
      await load();
    } catch (e) {
      if (handle401(e)) return;
      // ECONNABORTED with `timeout of Xms exceeded` = frontend hit its
      // own axios timeout; anything else with no response = ingress
      // gateway timeout. Both leave the draft in `pending_sap_write`
      // limbo — SAP may or may not have accepted the write.
      const isTimeout = e.code === "ECONNABORTED"
                        || /timeout/i.test(e.message || "")
                        || e.response?.status === 504;
      if (isTimeout) {
        toast.error(
          "SAP is taking longer than usual — the write may still have gone through. "
          + "Refresh the page in about a minute; if the draft is still marked "
          + "sap_error, click Approve again to retry.",
          { duration: 20000, style: { maxWidth: 620 } });
        await load();  // re-fetch so the approver sees the current draft state
      } else {
        // Prefer the backend's `detail` (FastAPI HTTPException), then
        // any user_message/message the write pipeline attaches, then
        // the axios error message, and only then fall back to the
        // generic "Decision failed" string.
        const rd = e.response?.data;
        const msg = (rd && (rd.detail || rd.user_message || rd.message))
                     || e.message
                     || "Decision failed";
        toast.error(msg, { duration: 15000, style: { maxWidth: 620 } });
      }
    } finally {
      setBusy(null);
    }
  };

  // Progress tracking for the bulk-approve modal. `total` triggers the
  // popup; per-chunk updates give the user real-time X/Y visibility so
  // they don't think the app has frozen while N SOAP writes run.
  const [bulkProgress, setBulkProgress] = useState(null);
  //   { total, done, released, in_prep, failed, decision, current }

  const runBulk = async (decision) => {
    const items = rows
      .filter((r) => selected.has(r.draft_id))
      .map((r) => ({ draft_id: r.draft_id, decision,
                     comment: bulkComment || null,
                     material_code: r.material_code,
                     supplier_code: r.supplier_code }));
    if (items.length === 0) return;
    setBusy("__bulk__");
    setBulkProgress({ total: items.length, done: 0,
                      released: 0, in_prep: 0, failed: 0,
                      decision, current: items[0], failures: [] });
    const CHUNK = 5; // one SOAP round-trip per chunk on the backend
    try {
      for (let i = 0; i < items.length; i += CHUNK) {
        const chunk = items.slice(i, i + CHUNK);
        setBulkProgress((p) => ({ ...p, current: chunk[0] }));
        const r = await axios.post(
          `${API}/list-prices/approvals/bulk`,
          { items: chunk.map(({ draft_id, decision, comment }) =>
                        ({ draft_id, decision, comment })) },
          { headers: authHeaders(), timeout: 120000 });
        const results = r.data.results || [];
        setBulkProgress((p) => {
          const failures = [...(p.failures || [])];
          let rel = p.released, inp = p.in_prep, fail = p.failed;
          for (const res of results) {
            if (!res.ok) {
              fail += 1;
              failures.push({
                draft_id: res.draft_id,
                message: res.user_message || res.sap_note
                          || res.fault || res.error || "unknown",
              });
            } else if (res.released) rel += 1;
            else if (res.new_status === "sap_written_in_prep") inp += 1;
            else rel += 1;  // non-SAP-write approves (L1→L2)
          }
          return { ...p, done: p.done + results.length,
                   released: rel, in_prep: inp, failed: fail,
                   failures };
        });
      }
      await load();
    } catch (e) {
      if (handle401(e)) return;
      toast.error(e.response?.data?.detail || "Bulk failed");
      setBulkProgress((p) => p && { ...p, aborted: true });
    } finally {
      setBusy(null);
      setBulkMode(null);
      setBulkComment("");
    }
  };

  const toggleRow = (draft_id) => {
    const next = new Set(selected);
    if (next.has(draft_id)) next.delete(draft_id); else next.add(draft_id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.draft_id)));
  };

  const canAct = view === "awaiting_me";
  const selectedCount = selected.size;

  // Mobile-native branch — swaps the desktop JDE grid for a
  // card/tab/sticky-bar UX below 768 px. Reuses ALL state / API hooks
  // above; this is a pure render swap. Desktop path continues unchanged
  // for laptop / tablet users.
  const isMobile = useMediaQuery("(max-width: 767px)");
  if (isMobile) {
    return (
      <>
        <MobileApprovalsView
          view={view === "history" ? "awaiting_me" : view}
          setView={setView}
          rows={rows} loading={loading}
          canAct={canAct}
          selected={selected}
          toggleOne={toggleRow}
          toggleAll={toggleAll}
          selectedCount={selectedCount}
          busy={busy}
          onApprove={(r) => decideOne(r, "approve")}
          onReject={(r) => setRejectFor(r)}
          onBulk={(mode) => setBulkMode(mode)}
          onRefresh={load}
          hasHistory={false}
        />
        {/* Reject / bulk / progress modals reuse the same JSX as desktop;
            they're portal-mounted so they overlay the mobile view cleanly. */}
        {rejectFor && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-3"
               data-testid="m-lp-reject-modal">
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-lg p-4 space-y-3">
              <div className="text-base font-bold text-slate-900">
                Reject draft {rejectFor.short_id || rejectFor.draft_id?.slice(-6)}
              </div>
              <div className="text-xs text-slate-600 leading-snug">
                <span className="font-mono">{rejectFor.supplier_code}</span> · {rejectFor.supplier_name}
                <br />→ <span className="font-mono">{rejectFor.material_code}</span>
              </div>
              <Textarea rows={4} value={rejectComment}
                         onChange={(e) => setRejectComment(e.target.value)}
                         placeholder="Reason for rejection"
                         className="text-base"
                         data-testid="m-lp-reject-comment" />
              <div className="flex gap-2 pt-1">
                <button type="button"
                        className="flex-1 min-h-[48px] rounded-md border border-slate-300 bg-white font-bold text-slate-700"
                        onClick={() => { setRejectFor(null); setRejectComment(""); }}>
                  Cancel
                </button>
                <button type="button"
                        className="flex-1 min-h-[48px] rounded-md bg-rose-600 text-white font-bold"
                        onClick={async () => {
                          const d = rejectFor;
                          const c = rejectComment;
                          setRejectFor(null); setRejectComment("");
                          await decideOne(d, "reject", c);
                        }}
                        data-testid="m-lp-reject-confirm">
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}
        {bulkMode && (
          <div className="fixed inset-0 bg-slate-900/50 flex items-end sm:items-center justify-center z-50 p-3"
               data-testid="m-lp-bulk-modal">
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full max-w-lg p-4 space-y-3">
              <div className="text-base font-bold text-slate-900">
                Bulk {bulkMode} — {selectedCount} draft(s)
              </div>
              <Textarea rows={3} value={bulkComment}
                         onChange={(e) => setBulkComment(e.target.value)}
                         placeholder={bulkMode === "reject" ? "Reason (recommended)" : "Comment (optional)"}
                         data-testid="m-lp-bulk-comment" />
              <div className="flex gap-2 pt-1">
                <button type="button"
                        className="flex-1 min-h-[48px] rounded-md border border-slate-300 bg-white font-bold text-slate-700"
                        onClick={() => { setBulkMode(null); setBulkComment(""); }}>
                  Cancel
                </button>
                <button type="button"
                        className={`flex-1 min-h-[48px] rounded-md text-white font-bold ${bulkMode === "approve" ? "bg-cyan-700" : "bg-rose-600"}`}
                        onClick={() => runBulk(bulkMode)}
                        data-testid="m-lp-bulk-confirm">
                  Confirm {bulkMode}
                </button>
              </div>
            </div>
          </div>
        )}
        {bulkProgress && (
          <BulkProgressModal
            progress={bulkProgress}
            onClose={() => setBulkProgress(null)} />
        )}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1400px] mx-auto p-3 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-1 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => nav("/sap")}
                    data-testid="lpa-back-sap"
                    className="px-2 sm:px-3">
              <ArrowLeft size={16} className="sm:mr-1" />
              <span className="hidden sm:inline">SAP Workspace</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => nav("/home")}
                    data-testid="lpa-back-home"
                    className="px-2 sm:px-3">
              <Home size={16} className="sm:mr-1" />
              <span className="hidden sm:inline">Home</span>
            </Button>
            <div className="flex items-center gap-2 text-teal-700 ml-1 sm:ml-2">
              <ClipboardList size={16} className="sm:hidden" />
              <ClipboardList size={18} className="hidden sm:block" />
              <h1 className="text-fiori-h2 text-slate-800 whitespace-nowrap">
                List Price Approvals
              </h1>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={load}
                  disabled={loading} data-testid="lpa-refresh-btn">
            <RefreshCw size={13} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>

        {/* View tabs — labels shorten on mobile, full form on ≥sm. */}
        <div className="inline-flex rounded-sm border border-slate-200 bg-white overflow-hidden text-xs sm:text-sm">
          <button
            onClick={() => setView("awaiting_me")}
            className={`px-2 sm:px-4 py-1.5 font-medium whitespace-nowrap ${view === "awaiting_me" ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            data-testid="lpa-tab-awaiting"
          >
            <span className="sm:hidden">Awaiting</span>
            <span className="hidden sm:inline">Awaiting my approval</span>
          </button>
          <button
            onClick={() => setView("my_submissions")}
            className={`px-2 sm:px-4 py-1.5 font-medium border-l border-slate-200 whitespace-nowrap ${view === "my_submissions" ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            data-testid="lpa-tab-submissions"
          >
            <span className="sm:hidden">Submitted</span>
            <span className="hidden sm:inline">Submitted by me</span>
          </button>
          <button
            onClick={() => setView("history")}
            className={`px-2 sm:px-4 py-1.5 font-medium border-l border-slate-200 flex items-center gap-1 whitespace-nowrap ${view === "history" ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            data-testid="lpa-tab-history"
          >
            <History size={13} /> History
          </button>
        </div>

        {/* Bulk action bar */}
        {canAct && selectedCount > 0 && (
          <div className="bg-slate-800 text-white rounded-sm px-4 py-3 flex items-center justify-between"
               data-testid="lpa-bulk-bar">
            <div className="text-sm">
              <span className="font-bold">{selectedCount}</span> selected
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" className="text-white hover:bg-slate-700"
                      onClick={() => setSelected(new Set())}>Clear</Button>
              <Button size="sm" className="bg-rose-600 hover:bg-rose-700"
                      onClick={() => setBulkMode("reject")}
                      data-testid="lpa-bulk-reject-btn">
                <XCircle size={13} className="mr-1" /> Reject selected
              </Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => setBulkMode("approve")}
                      disabled={busy === "__bulk__"}
                      data-testid="lpa-bulk-approve-btn">
                {busy === "__bulk__" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <CheckCircle2 size={13} className="mr-1" />}
                Approve selected
              </Button>
            </div>
          </div>
        )}

        {bulkMode && (
          <div className="bg-white border border-slate-300 rounded-sm p-4 space-y-3"
               data-testid="lpa-bulk-confirm">
            <div className="text-sm font-semibold text-slate-800">
              {bulkMode === "approve"
                ? `Approve ${selectedCount} draft(s) — each will push to SAP inline`
                : `Reject ${selectedCount} draft(s)`}
            </div>
            <Textarea rows={2} value={bulkComment}
                       onChange={(e) => setBulkComment(e.target.value)}
                       placeholder={bulkMode === "reject" ? "Reason (recommended)" : "Comment (optional)"}
                       data-testid="lpa-bulk-comment" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost"
                      onClick={() => { setBulkMode(null); setBulkComment(""); }}>Cancel</Button>
              <Button size="sm"
                      className={bulkMode === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}
                      onClick={() => runBulk(bulkMode)}
                      disabled={busy === "__bulk__"}
                      data-testid="lpa-bulk-submit">
                {busy === "__bulk__" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Send size={13} className="mr-1" />}
                Confirm {bulkMode}
              </Button>
            </div>
          </div>
        )}

        {bulkProgress && (
          <BulkProgressModal
            progress={bulkProgress}
            onClose={() => setBulkProgress(null)} />
        )}

        {view === "history" && (
          <HistoryFilters
            scope={histScope} setScope={setHistScope}
            q={histQ} setQ={setHistQ}
            from={histFrom} setFrom={setHistFrom}
            to={histTo} setTo={setHistTo}
            onApply={load}
            loading={loading}
            total={histTotal}
          />
        )}

        {loading ? (
          <div className="bg-white rounded-sm border border-slate-200 p-8 flex items-center justify-center gap-2 text-slate-600"
               data-testid="lpa-loading">
            <Loader2 size={16} className="animate-spin" />
            Loading {view === "history" ? "history" : "queue"}…
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-sm border border-slate-200 p-8 text-center text-slate-600"
               data-testid="lpa-empty">
            <ClipboardList size={28} className="mx-auto mb-2 text-slate-300" />
            <div className="text-sm font-semibold text-slate-700">
              {view === "awaiting_me"
                ? "No drafts waiting for you"
                : view === "my_submissions"
                ? "You haven't submitted any drafts yet"
                : histScope === "mine"
                ? "You haven't approved any list prices yet"
                : "No list-price approvals match your filters"}
            </div>
            <div className="text-xs mt-1">
              {view === "awaiting_me"
                ? "You're all caught up. New drafts will appear here as they're submitted."
                : view === "my_submissions"
                ? "Head to List Prices to create one."
                : "Try clearing the filters above or switching to org-wide scope."}
            </div>
          </div>
        ) : view === "history" ? (
          <HistoryTable rows={rows} />
        ) : (
          <ApprovalGrid
            rows={rows}
            canAct={canAct}
            selected={selected}
            selectedCount={selectedCount}
            toggleAll={toggleAll}
            toggleRow={toggleRow}
            busy={busy}
            onApprove={(r) => decideOne(r, "approve")}
            onReject={(r) => setRejectFor(r)}
          />
        )}

        {/* Reject-with-comment modal */}
        {rejectFor && (
          <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50"
               data-testid="lpa-reject-modal">
            <div className="bg-white rounded-sm shadow-xl max-w-lg w-full p-5 space-y-3 border border-slate-200">
              <div className="text-fiori-h2 text-slate-800">
                Reject draft {rejectFor.draft_id}
              </div>
              <div className="text-xs text-slate-600">
                {rejectFor.supplier_code} · {rejectFor.supplier_name} → {rejectFor.material_code}
                <br />Requested: <span className="font-semibold">{fmt(rejectFor.amount)} {rejectFor.currency_code}</span>
              </div>
              <Textarea rows={3} value={rejectComment}
                         onChange={(e) => setRejectComment(e.target.value)}
                         placeholder="Reason for rejection"
                         data-testid="lpa-reject-comment" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost"
                        onClick={() => { setRejectFor(null); setRejectComment(""); }}>
                  Cancel
                </Button>
                <Button size="sm" className="bg-rose-600 hover:bg-rose-700"
                        onClick={async () => {
                          const d = rejectFor;
                          const c = rejectComment;
                          setRejectFor(null); setRejectComment("");
                          await decideOne(d, "reject", c);
                        }}
                        data-testid="lpa-reject-confirm">
                  <XCircle size={13} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ApprovalGrid({ rows, canAct, selected, selectedCount, toggleAll,
                          toggleRow, busy, onApprove, onReject }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const [sort, toggleSort] = useSort(null, "desc");
  const extractors = {
    draft:     (r) => r.short_id || r.draft_id,
    status:    (r) => r.status,
    supplier:  (r) => r.supplier_code,
    material:  (r) => r.material_code,
    requested: (r) => Number(r.display_amount ?? r.amount ?? 0) / (Number(r.display_base_quantity ?? r.base_quantity ?? 1) || 1),
    standing:  (r) => r.current_standing?.amount ?? null,
    delta:     (r) => {
      const a = Number(r.display_amount ?? r.amount ?? 0) / (Number(r.display_base_quantity ?? r.base_quantity ?? 1) || 1);
      const b = Number(r.current_standing?.amount ?? 0) / (Number(r.current_standing?.base_quantity ?? 1) || 1);
      return b > 0 ? ((a - b) / b) * 100 : null;
    },
    lowest:    (r) => r.lowest_offer?.amount ?? null,
    lastPo:    (r) => r.last_po_price?.amount ?? null,
    validity:  (r) => r.valid_from || "",
    by:        (r) => r.created_by_name || r.created_by || "",
  };
  const sortedRows = applySort(rows, sort, extractors);

  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden"
         data-testid="lpa-grid">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-100 border-b border-slate-300 text-fiori-caption uppercase tracking-wider text-slate-600 sticky top-0">
            <tr>
              {canAct && (
                <th className="w-8 px-1.5 py-1 text-left">
                  <input type="checkbox"
                         checked={selectedCount === rows.length && rows.length > 0}
                         onChange={toggleAll}
                         data-testid="lpa-select-all" />
                </th>
              )}
              <th className="w-6 px-1 py-1.5"></th>
              <SortableTH label="Draft"     sortKey="draft"     sort={sort} onToggle={toggleSort} testid="lpa-th-draft" />
              <SortableTH label="Status"    sortKey="status"    sort={sort} onToggle={toggleSort} testid="lpa-th-status" />
              <SortableTH label="Supplier"  sortKey="supplier"  sort={sort} onToggle={toggleSort} testid="lpa-th-supplier" />
              <SortableTH label="Material"  sortKey="material"  sort={sort} onToggle={toggleSort} testid="lpa-th-material" />
              <SortableTH label="Requested" sortKey="requested" sort={sort} onToggle={toggleSort} align="right" testid="lpa-th-requested" />
              <SortableTH label="Standing"  sortKey="standing"  sort={sort} onToggle={toggleSort} align="right" testid="lpa-th-standing" />
              <SortableTH label="Δ%"        sortKey="delta"     sort={sort} onToggle={toggleSort} align="right" testid="lpa-th-delta" />
              <SortableTH label="Lowest"    sortKey="lowest"    sort={sort} onToggle={toggleSort} align="right" testid="lpa-th-lowest" />
              <SortableTH label="Last PO"   sortKey="lastPo"    sort={sort} onToggle={toggleSort} align="right" testid="lpa-th-lastpo" />
              <SortableTH label="Validity"  sortKey="validity"  sort={sort} onToggle={toggleSort} testid="lpa-th-validity" />
              <SortableTH label="By"        sortKey="by"        sort={sort} onToggle={toggleSort} testid="lpa-th-by" />
              {canAct && <th className="text-right px-1.5 py-1 font-semibold w-40">Action</th>}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => (
              <ApprovalRow
                key={r.draft_id}
                row={r}
                canAct={canAct}
                selected={selected.has(r.draft_id)}
                onToggle={() => toggleRow(r.draft_id)}
                busy={busy === r.draft_id}
                onApprove={() => onApprove(r)}
                onReject={() => onReject(r)}
                expanded={expanded.has(r.draft_id)}
                onToggleExpand={() => toggleExpand(r.draft_id)}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApprovalRow({ row, canAct, selected, onToggle, busy, onApprove,
                        onReject, expanded, onToggleExpand }) {
  const requestedAmt = Number(row.display_amount ?? row.amount ?? 0);
  const requestedBq  = Number(row.display_base_quantity ?? row.base_quantity ?? 1) || 1;
  const requestedPerUnit = requestedAmt / requestedBq;
  const standingAmt = row.current_standing?.amount;
  const standingBq  = Number(row.current_standing?.base_quantity ?? 1) || 1;
  const standingPerUnit = standingAmt != null ? Number(standingAmt) / standingBq : null;
  const lowestAmt   = row.lowest_offer?.amount;
  const lowestBq    = Number(row.lowest_offer?.base_quantity ?? 1) || 1;
  const lastPoAmt   = row.last_po_price?.amount;
  const lastPoBq    = Number(row.last_po_price?.base_quantity ?? 1) || 1;
  const cur = row.currency_code || "INR";
  const deltaPct = (standingPerUnit && requestedPerUnit)
    ? ((requestedPerUnit - standingPerUnit) / standingPerUnit) * 100
    : null;

  const statusPill = row.status === "pending_approval" ? "PENDING"
    : row.status === "pending_l1" ? "L1"
    : row.status === "pending_l2" ? "L2"
    : row.status === "pending_sap_write" ? "WRITING…"
    : row.status === "sap_written" ? "WRITTEN"
    : row.status === "sap_written_in_prep" ? "IN PREP"
    : row.status === "sap_error" ? "SAP ERR"
    : row.status;
  const statusColor = row.status === "sap_error" ? "bg-rose-100 text-rose-800 border-rose-200"
    : row.status === "sap_written" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : row.status === "sap_written_in_prep" ? "bg-indigo-100 text-indigo-800 border-indigo-200"
    : "bg-amber-50 text-amber-800 border-amber-200";

  const priceCell = (amt, bq, ccy, unit) => (
    <PriceText amt={amt} bq={bq} ccy={ccy} unit={unit} />
  );

  return (
    <>
      <tr className={`border-b border-slate-100 hover:bg-slate-50 ${selected ? "bg-teal-50" : ""}`}
          data-testid={`lpa-card-${row.draft_id}`}>
        {canAct && (
          <td className="px-1.5 py-1">
            <input type="checkbox" checked={selected} onChange={onToggle}
                   data-testid={`lpa-select-${row.draft_id}`} />
          </td>
        )}
        <td className="px-1 py-1.5">
          <button onClick={onToggleExpand}
                  className="text-slate-500 hover:text-slate-800"
                  aria-label="toggle detail row"
                  data-testid={`lpa-expand-${row.draft_id}`}>
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </td>
        <td className="px-1.5 py-1 mono font-semibold text-slate-800"
            title={row.draft_id}
            data-testid={`lpa-short-${row.draft_id}`}>
          {row.short_id || row.draft_id}
        </td>
        <td className="px-1.5 py-1">
          <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${statusColor}`}>
            {statusPill}
          </span>
          {(() => {
            const l1 = (row.approval_history || [])
              .find((h) => (h.stage === "l1" || h.stage === "pending_l1")
                             && h.decision === "approve");
            if (!l1) return null;
            const fullTip = [
              l1.by_name && l1.by_emp_no ? `${l1.by_name} | ${l1.by_emp_no}` : (l1.by_name || l1.by_emp_no),
              l1.ts ? `on ${fmtDate(l1.ts)}` : "",
              l1.comment ? `— ${l1.comment}` : "",
            ].filter(Boolean).join(" ");
            return (
              <div className="text-[10px] text-slate-500 mt-0.5 leading-tight"
                   data-testid={`lpa-l1-audit-${row.draft_id}`}
                   title={`Authorised by ${fullTip}`}>
                <span className="font-semibold text-slate-700">
                  {l1.by_name || l1.by_emp_no}
                </span>
                {l1.ts && <span className="text-slate-400"> · {fmtDate(l1.ts)}</span>}
              </div>
            );
          })()}
        </td>
        <td className="px-1.5 py-1 mono text-slate-800 max-w-[130px]">
          <div className="font-semibold">{row.supplier_code}</div>
          {row.supplier_name && (
            <div className="text-[10px] text-slate-500 non-mono truncate"
                 title={row.supplier_name}>
              {row.supplier_name}
            </div>
          )}
        </td>
        <td className="px-1.5 py-1 mono text-slate-800 max-w-[150px]">
          <div className="font-semibold">{row.material_code}</div>
          {row.material_desc && (
            <div className="text-[10px] text-slate-500 non-mono truncate"
                 title={row.material_desc}>
              {row.material_desc}
            </div>
          )}
        </td>
        <td className="px-1.5 py-1 text-right whitespace-nowrap">
          {priceCell(requestedAmt, requestedBq, cur, row.unit_code)}
        </td>
        <td className="px-1.5 py-1 text-right whitespace-nowrap">
          {priceCell(standingAmt, standingBq, row.current_standing?.currency_code, row.current_standing?.unit_code)}
        </td>
        <td className="px-1.5 py-1 text-right whitespace-nowrap">
          {deltaPct == null ? <span className="text-slate-400">—</span> : (
            <span className={`inline-flex items-center gap-0.5 font-semibold ${
              deltaPct > 0.5 ? "text-rose-700" : deltaPct < -0.5 ? "text-emerald-700" : "text-slate-500"
            }`}>
              {deltaPct > 0.5 ? <TrendingUp size={10} /> : deltaPct < -0.5 ? <TrendingDown size={10} /> : <Minus size={10} />}
              {deltaPct > 0 ? "+" : ""}{deltaPct.toFixed(1)}%
            </span>
          )}
        </td>
        <td className="px-1.5 py-1 text-right whitespace-nowrap">
          {priceCell(lowestAmt, lowestBq, row.lowest_offer?.currency_code, row.lowest_offer?.unit_code)}
        </td>
        <td className="px-1.5 py-1 text-right whitespace-nowrap">
          {priceCell(lastPoAmt, lastPoBq, row.last_po_price?.currency_code, row.last_po_price?.unit_code)}
        </td>
        <td className="px-1.5 py-1 whitespace-nowrap text-slate-600 text-[11px]"
            title={`Valid ${fmtDate(row.valid_from)} → ${row.valid_to === "9999-12-31" ? "open" : fmtDate(row.valid_to)}`}>
          <div>{fmtDate(row.valid_from)}</div>
          <div className="text-[10px] text-slate-400">→ {row.valid_to === "9999-12-31" ? "open" : fmtDate(row.valid_to)}</div>
        </td>
        <td className="px-1.5 py-1 text-slate-600 whitespace-nowrap max-w-[140px]">
          <div className="font-semibold text-slate-700 truncate"
               title={`${row.created_by_name || row.created_by} · ${row.created_by || ""}`}>
            {row.created_by_name || row.created_by}
          </div>
          <div className="text-[10px] text-slate-400">{fmtDate(row.created_at)}</div>
        </td>
        {canAct && (
          <td className="px-1.5 py-1 text-right whitespace-nowrap"
              data-testid={`lpa-actions-${row.draft_id}`}>
            <div className="inline-flex gap-1">
              <Button size="sm"
                      className="h-6 px-1.5 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={onApprove} disabled={busy}
                      title={row.status === "sap_written_in_prep"
                        ? "Fires a UUID-targeted release update in SAP (no duplicate spec created)"
                        : row.status === "sap_error"
                        ? "Retries the SAP write with the current draft values"
                        : "Approve this draft and advance it in the workflow"}
                      data-testid={`lpa-approve-${row.draft_id}`}>
                {busy ? <Loader2 size={10} className="animate-spin" />
                      : row.status === "sap_written_in_prep"
                      ? <><Send size={10} className="mr-0.5" /> Release</>
                      : row.status === "sap_error"
                      ? <><RefreshCw size={10} className="mr-0.5" /> Retry</>
                      : <><CheckCircle2 size={10} className="mr-0.5" /> Approve</>}
              </Button>
              <Button size="sm" variant="outline"
                      className="h-6 px-1.5 text-[10px] border-rose-300 text-rose-700 hover:bg-rose-50"
                      onClick={onReject} disabled={busy}
                      data-testid={`lpa-reject-${row.draft_id}`}>
                <XCircle size={10} className="mr-0.5" /> Reject
              </Button>
            </div>
          </td>
        )}
      </tr>
      {expanded && (
        <tr className="bg-slate-50 border-b border-slate-200"
            data-testid={`lpa-detail-${row.draft_id}`}>
          <td colSpan={canAct ? 14 : 13} className="px-6 py-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-fiori-caption uppercase tracking-wider text-slate-400 font-semibold mb-1">
                  Full identifiers
                </div>
                <div className="mono text-slate-700">Short: <b>{row.short_id || "—"}</b></div>
                <div className="mono text-slate-700">Draft: {row.draft_id}</div>
                <div className="mono text-[10px] text-slate-500">status = {row.status}</div>
                {row.sap_response_uuid && (
                  <div className="mono text-[10px] text-slate-500 break-all">SAP UUID: {row.sap_response_uuid}</div>
                )}
              </div>
              <div>
                <div className="text-fiori-caption uppercase tracking-wider text-slate-400 font-semibold mb-1">
                  Supplier &amp; material
                </div>
                <div>{row.supplier_code} · {row.supplier_name || "—"}</div>
                <div>{row.material_code} · {row.material_desc || "—"}</div>
                {row.notes && <div className="mt-1 italic text-slate-600 border-l-2 border-slate-300 pl-2">“{row.notes}”</div>}
              </div>
              <div>
                <div className="text-fiori-caption uppercase tracking-wider text-slate-400 font-semibold mb-1">
                  Approval trail ({row.approval_history?.length || 0})
                </div>
                {row.approval_history?.length > 0 ? (
                  <ul className="space-y-0.5">
                    {row.approval_history.map((h, i) => (
                      <li key={i}>
                        <span className="font-semibold">{stageLabel(h.stage)}</span>
                        {" · "}
                        <span className={h.decision === "reject" ? "text-rose-700 font-semibold" : "text-emerald-700 font-semibold"}>
                          {h.decision === "reject" ? "rejected" : "approved"}
                        </span>
                        {" by "}{h.by_name || h.by_emp_no}
                        {h.comment && <> · <span className="italic">“{h.comment}”</span></>}
                        {h.ts && <> · {fmtDate(h.ts)}</>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-slate-400 italic">No decisions yet</div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


function HistoryFilters({ scope, setScope, q, setQ, from, setFrom, to, setTo,
                          onApply, loading, total }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-3 space-y-3"
         data-testid="lpa-history-filters">
      <div className="flex flex-wrap items-end gap-3">
        {/* Scope toggle */}
        <div className="flex flex-col gap-1">
          <label className="text-fiori-caption uppercase tracking-wider text-slate-400 font-semibold">
            Scope
          </label>
          <div className="inline-flex rounded-sm border border-slate-200 overflow-hidden text-xs">
            <button
              onClick={() => setScope("mine")}
              className={`px-3 py-1.5 font-medium ${scope === "mine" ? "bg-slate-800 text-white" : "text-slate-700 hover:bg-slate-50"}`}
              data-testid="lpa-history-scope-mine">
              Only mine
            </button>
            <button
              onClick={() => setScope("all")}
              className={`px-3 py-1.5 font-medium border-l border-slate-200 ${scope === "all" ? "bg-slate-800 text-white" : "text-slate-700 hover:bg-slate-50"}`}
              data-testid="lpa-history-scope-all">
              Org-wide
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
          <label className="text-fiori-caption uppercase tracking-wider text-slate-400 font-semibold">
            Search (supplier · material · draft id)
          </label>
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onApply()}
              placeholder="e.g. S3219, SCR755WM, LP-20260922-…"
              className="pl-7 h-8 text-sm"
              data-testid="lpa-history-search"
            />
          </div>
        </div>

        {/* Date from */}
        <div className="flex flex-col gap-1">
          <label className="text-fiori-caption uppercase tracking-wider text-slate-400 font-semibold">
            Date from
          </label>
          <Input type="date" value={from}
                 onChange={(e) => setFrom(e.target.value)}
                 className="h-8 text-sm w-[160px]"
                 data-testid="lpa-history-from" />
        </div>

        {/* Date to */}
        <div className="flex flex-col gap-1">
          <label className="text-fiori-caption uppercase tracking-wider text-slate-400 font-semibold">
            Date to
          </label>
          <Input type="date" value={to}
                 onChange={(e) => setTo(e.target.value)}
                 className="h-8 text-sm w-[160px]"
                 data-testid="lpa-history-to" />
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={onApply} disabled={loading}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="lpa-history-apply">
            {loading ? <Loader2 size={13} className="mr-1 animate-spin" />
                     : <Search size={13} className="mr-1" />}
            Apply
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => { setQ(""); setFrom(""); setTo(""); setTimeout(onApply, 0); }}
                  data-testid="lpa-history-clear">
            Clear
          </Button>
        </div>
      </div>
      <div className="text-fiori-caption text-slate-500">
        Showing SAP-written approvals only · <span className="font-semibold text-slate-700">{total}</span> record(s)
      </div>
    </div>
  );
}

function decisionPill(myDecision, stage) {
  // `myDecision` is this user's own approval_history entry (or null).
  // Falls back to using the last stage the draft passed through when
  // scope=all and the user wasn't in the trail.
  if (!myDecision) {
    return { label: "—", cls: "bg-slate-100 text-slate-500 border-slate-200" };
  }
  const s = myDecision.stage || stage || "";
  const d = myDecision.decision || "approve";
  if (d === "reject") {
    return { label: "Rejected", cls: "bg-rose-100 text-rose-800 border-rose-200" };
  }
  if (s === "pending_l1")             return { label: "L1 approve",    cls: "bg-teal-100 text-teal-800 border-teal-200" };
  if (s === "pending_l2")             return { label: "L2 approve",    cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  if (s === "pending_approval")       return { label: "Approved",       cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  if (s === "sap_error")              return { label: "Retry approve",  cls: "bg-indigo-100 text-indigo-800 border-indigo-200" };
  if (s === "sap_written_in_prep")    return { label: "Retry approve",  cls: "bg-indigo-100 text-indigo-800 border-indigo-200" };
  return { label: "Approved", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
}

function HistoryTable({ rows }) {
  const [sort, toggleSort] = useSort(null, "desc");
  const extractors = {
    approvedOn: (r) => r.sap_write_ts || r.my_decision?.ts || r.created_at,
    supplier:   (r) => r.supplier_code,
    material:   (r) => r.material_code,
    price:      (r) => Number(r.display_amount ?? r.amount ?? 0),
    currency:   (r) => r.currency_code,
    decision:   (r) => r.my_decision?.decision || r.status,
    sapStatus:  (r) => r.sap_released ? "released" : (r.status || ""),
    specId:     (r) => r.sap_price_spec_id || "",
    comment:    (r) => r.my_decision?.comment || r.notes || "",
  };
  const sortedRows = applySort(rows, sort, extractors);
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden"
         data-testid="lpa-history-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-fiori-caption uppercase tracking-wider text-slate-500">
            <tr>
              <SortableTH label="Approved on"       sortKey="approvedOn" sort={sort} onToggle={toggleSort} testid="lph-th-approvedOn" />
              <SortableTH label="Supplier"          sortKey="supplier"   sort={sort} onToggle={toggleSort} testid="lph-th-supplier" />
              <SortableTH label="Material"          sortKey="material"   sort={sort} onToggle={toggleSort} testid="lph-th-material" />
              <SortableTH label="Price"             sortKey="price"      sort={sort} onToggle={toggleSort} align="right" testid="lph-th-price" />
              <SortableTH label="Currency"          sortKey="currency"   sort={sort} onToggle={toggleSort} testid="lph-th-currency" />
              <SortableTH label="My decision"       sortKey="decision"   sort={sort} onToggle={toggleSort} testid="lph-th-decision" />
              <SortableTH label="SAP status"        sortKey="sapStatus"  sort={sort} onToggle={toggleSort} testid="lph-th-sapstatus" />
              <SortableTH label="SAP Price Spec ID" sortKey="specId"     sort={sort} onToggle={toggleSort} testid="lph-th-specid" />
              <SortableTH label="Comment"           sortKey="comment"    sort={sort} onToggle={toggleSort} testid="lph-th-comment" />
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => {
              const pill = decisionPill(r.my_decision, r.status);
              // For drafts that never reached SAP yet (still pending),
              // show the actual workflow status instead of the
              // Released / In-preparation SAP-write dichotomy.
              const reachedSap = r.status === "sap_written" || r.status === "sap_written_in_prep";
              const sapReleased = r.sap_released;
              const sapStatusLabel = reachedSap
                ? (sapReleased ? "Released" : "In preparation")
                : (r.status === "pending_l2" ? "Pending L2"
                    : r.status === "pending_l1" ? "Pending L1"
                    : r.status === "pending_approval" ? "Pending approval"
                    : r.status === "sap_error" ? "SAP error"
                    : r.status === "rejected" ? "Rejected"
                    : r.status || "—");
              const sapStatusCls = !reachedSap
                ? (r.status === "rejected" ? "bg-rose-100 text-rose-800 border-rose-200"
                    : r.status === "sap_error" ? "bg-rose-50 text-rose-700 border-rose-200"
                    : "bg-slate-100 text-slate-700 border-slate-200")
                : sapReleased
                  ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                  : "bg-amber-50 text-amber-800 border-amber-200";
              const disp = r.display_amount != null ? r.display_amount : r.amount;
              const bq = r.display_base_quantity || r.base_quantity || 1;
              const comment = r.my_decision?.comment || r.notes || "—";
              return (
                <tr key={r.draft_id} className="border-b border-slate-100 hover:bg-slate-50"
                    data-testid={`lpa-history-row-${r.draft_id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                    {fmtDate(r.sap_write_ts || r.my_decision?.ts || r.created_at)}
                    <div className="mono text-xs text-slate-800 font-semibold"
                         title={r.draft_id}>
                      {r.short_id || r.draft_id}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="mono text-xs text-slate-800">{r.supplier_code}</div>
                    <div className="text-fiori-caption text-slate-500 max-w-[220px] truncate">
                      {r.supplier_name || "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="mono text-xs text-slate-800">{r.material_code}</div>
                    <div className="text-fiori-caption text-slate-500 max-w-[240px] truncate">
                      {r.material_desc || "—"}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right text-slate-800">
                    <PriceText amt={disp} bq={bq} unit={r.unit_code}
                               testid={`lpa-history-price-${r.draft_id}`} />
                  </td>
                  <td className="px-3 py-2 text-slate-700">{r.currency_code || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`text-fiori-caption font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${pill.cls}`}>
                      {pill.label}
                    </span>
                    {r.my_decision?.by_emp_no && (
                      <div className="text-fiori-caption text-slate-400 mt-0.5">
                        by {r.my_decision.by_name || r.my_decision.by_emp_no}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-fiori-caption font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${sapStatusCls}`}>
                      {sapStatusLabel}
                    </span>
                  </td>
                  <td className="px-3 py-2 mono text-fiori-caption text-slate-600 max-w-[220px] truncate"
                      title={r.sap_price_spec_id || ""}>
                    {r.sap_price_spec_id || "—"}
                  </td>
                  <td className="px-3 py-2 text-slate-600 text-fiori-caption max-w-[280px] truncate"
                      title={comment}>
                    {comment}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/** Live progress modal shown while a bulk approval fires the SAP writes.
 *  Renders as a centered overlay with:
 *   • a "Writing X of Y prices to SAP..." headline
 *   • a green progress bar (percent complete)
 *   • real-time counts: Released / In-prep / Failed
 *   • the current draft's supplier+material so the user knows the app is alive
 *   • a summary + Close button once done, listing per-row failures if any
 */
function BulkProgressModal({ progress, onClose }) {
  const done = progress.done || 0;
  const total = progress.total || 0;
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const finished = done >= total || progress.aborted;
  const decVerb = progress.decision === "reject" ? "Rejecting" : "Approving";
  const decDone = progress.decision === "reject" ? "Rejected" : "Approved";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
         data-testid="lpa-bulk-progress-modal">
      <div className="bg-white rounded-md shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden">
        <div className={`px-4 py-3 border-b border-slate-200 flex items-center gap-2 ${
              finished ? (progress.failed ? "bg-amber-50" : "bg-emerald-50") : "bg-teal-50"}`}>
          {finished
            ? (progress.failed ? <AlertTriangle size={16} className="text-amber-700" />
                               : <CheckCircle2 size={16} className="text-emerald-700" />)
            : <Loader2 size={16} className="animate-spin text-teal-700" />}
          <div className="text-sm font-semibold text-slate-800">
            {finished
              ? `${decDone} ${done} of ${total} price(s)`
              : `${decVerb} price ${Math.min(done + 1, total)} of ${total} · writing to SAP…`}
          </div>
        </div>
        <div className="p-4 space-y-3">
          {/* progress bar */}
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full transition-all duration-300 ${
                  progress.failed && finished ? "bg-amber-500" : "bg-emerald-500"}`}
                 style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span className="mono font-semibold">{done} / {total}</span>
            <span>{pct}%</span>
          </div>
          {/* live counts */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-2">
              <div className="text-fiori-caption text-emerald-700">Released</div>
              <div className="text-lg font-bold text-emerald-900 num">{progress.released}</div>
            </div>
            <div className="rounded-sm border border-amber-200 bg-amber-50 p-2">
              <div className="text-fiori-caption text-amber-700">In preparation</div>
              <div className="text-lg font-bold text-amber-900 num">{progress.in_prep}</div>
            </div>
            <div className="rounded-sm border border-rose-200 bg-rose-50 p-2">
              <div className="text-fiori-caption text-rose-700">Failed</div>
              <div className="text-lg font-bold text-rose-900 num">{progress.failed}</div>
            </div>
          </div>
          {/* what we're doing right now */}
          {!finished && progress.current && (
            <div className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              Now writing: <span className="mono font-semibold">{progress.current.supplier_code}</span>
              {" → "}
              <span className="mono font-semibold">{progress.current.material_code}</span>
            </div>
          )}
          {/* failure list on completion */}
          {finished && progress.failures?.length > 0 && (
            <details className="rounded-sm border border-rose-200 bg-rose-50 p-2 text-xs">
              <summary className="cursor-pointer font-semibold text-rose-800">
                Show {progress.failures.length} failure(s)
              </summary>
              <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {progress.failures.map((f, i) => (
                  <li key={i} className="text-rose-900">
                    <span className="mono font-semibold">{f.draft_id}</span> — {f.message}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end">
          <Button size="sm"
                  onClick={onClose}
                  disabled={!finished}
                  className={finished ? "bg-teal-600 hover:bg-teal-700 text-white" : ""}
                  data-testid="lpa-bulk-progress-close">
            {finished ? "Close" : "Please wait…"}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Mobile-native card + page (viewport < 768 px). Reuses the same state
// hooks from the parent — this is a rendering-only branch, no duplicate
// data fetching. Follows /app/design_guidelines.json:
//   • 56 px sticky top bar (cyan-800), 64 px sticky bottom bar
//   • Underlined tab strip (Awaiting / Submitted / History), 48 px tall
//   • Cards per row with 4 price rows as key-value + full-width
//     50/50 Reject / Approve buttons at the bottom, both ≥ 44 px
//   • Bulk-approve bottom-sheet appears when checkboxes are selected
// ---------------------------------------------------------------------------
function MobileApprovalCard({ row, canAct, selected, onToggle, busy, onApprove, onReject }) {
  const fmt = (n) => (n == null || Number.isNaN(Number(n))) ? "—" : fmtUnit(n);
  // Match the same field shape the desktop `ApprovalRow` reads (line 735).
  const reqAmt = Number(row.display_amount ?? row.amount ?? 0);
  const reqBq  = Number(row.display_base_quantity ?? row.base_quantity ?? 1) || 1;
  const req = reqAmt > 0 ? (reqAmt / reqBq) : null;
  const stdAmt = Number(row.current_standing?.amount ?? 0);
  const stdBq  = Number(row.current_standing?.base_quantity ?? 1) || 1;
  const std = stdAmt > 0 ? (stdAmt / stdBq) : null;
  const lowAmt = Number(row.lowest_offer?.amount ?? 0);
  const lowBq  = Number(row.lowest_offer?.base_quantity ?? 1) || 1;
  const low = lowAmt > 0 ? (lowAmt / lowBq) : null;
  const lpAmt = Number(row.last_po_price?.amount ?? 0);
  const lpBq  = Number(row.last_po_price?.base_quantity ?? 1) || 1;
  const last = lpAmt > 0 ? (lpAmt / lpBq) : null;
  const uom = row.unit_code || row.current_standing?.unit_code || "";
  const ccy = row.currency_code || row.current_standing?.currency_code || "";
  const delta = (req != null && std != null && Number(std) !== 0)
                  ? ((Number(req) - Number(std)) / Number(std)) * 100 : null;
  const deltaColor = delta == null ? "text-slate-400"
                        : delta > 5 ? "text-rose-700"
                        : delta > 0 ? "text-amber-700"
                        : "text-emerald-700";
  const statusPill = (row.status || "").toLowerCase();
  const pillColor = statusPill.includes("err") ? "bg-rose-50 text-rose-700 border-rose-300"
                     : statusPill.includes("pending") ? "bg-amber-50 text-amber-700 border-amber-300"
                     : statusPill.includes("l1") ? "bg-cyan-50 text-cyan-800 border-cyan-300"
                     : statusPill.includes("l2") ? "bg-cyan-50 text-cyan-800 border-cyan-300"
                     : "bg-slate-50 text-slate-700 border-slate-300";
  const isBusy = busy === row.draft_id;
  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 space-y-2"
         data-testid={`m-lp-card-${row.draft_id}`}>
      <div className="flex items-start justify-between gap-2">
        <label className="flex items-start gap-2 min-h-[44px] flex-1 cursor-pointer select-none touch-manipulation">
          <input type="checkbox"
                 className="mt-1 h-5 w-5 accent-cyan-600 flex-shrink-0"
                 checked={selected}
                 onChange={() => onToggle(row.draft_id)}
                 disabled={!canAct}
                 data-testid={`m-lp-check-${row.draft_id}`} />
          <div className="flex-1 min-w-0">
            <div className="text-base font-bold text-slate-900 leading-snug truncate">{row.supplier_name || row.supplier_code}</div>
            <div className="text-xs text-slate-500 font-mono">{row.supplier_code} · {row.short_id || row.draft_id?.slice(-6)}</div>
            <div className="text-sm font-bold text-slate-800 mt-1 truncate">{row.material_code}</div>
            {row.material_desc && <div className="text-xs text-slate-500 leading-snug">{row.material_desc}</div>}
          </div>
        </label>
        <span className={`px-2 py-1 rounded border text-[10px] font-bold uppercase tracking-wide ${pillColor} whitespace-nowrap`}>
          {row.status || "—"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs pt-1 border-t border-slate-100">
        <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-wide font-bold">Requested</span><span className="font-mono font-bold text-slate-900 text-right"><PriceText amt={reqAmt > 0 ? reqAmt : null} bq={reqBq} ccy={ccy} unit={uom} testid={`m-lp-req-price-${row.draft_id}`} /></span></div>
        <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-wide font-bold">Standing</span><span className="font-mono text-slate-800">{fmt(std)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-wide font-bold">Lowest</span><span className="font-mono text-slate-800">{fmt(low)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500 uppercase tracking-wide font-bold">Last PO</span><span className="font-mono text-slate-800">{fmt(last)}</span></div>
      </div>
      {(row.valid_from || row.valid_to) && (
        <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100"
             data-testid={`m-lp-validity-${row.draft_id}`}>
          <span className="text-slate-500 uppercase tracking-wide font-bold">Valid</span>
          <span className="font-mono text-slate-700">
            {fmtDate(row.valid_from)} <span className="text-slate-400">→</span> {row.valid_to === "9999-12-31" ? <span className="text-emerald-700 font-bold">open</span> : fmtDate(row.valid_to)}
          </span>
        </div>
      )}
      {delta != null && (
        <div className={`text-xs font-bold ${deltaColor}`}>Δ {delta > 0 ? "+" : ""}{delta.toFixed(1)}% vs standing</div>
      )}
      {canAct && (
        <div className="flex gap-2 pt-2 border-t border-slate-100">
          <button type="button"
                  onClick={() => onReject(row)}
                  disabled={isBusy}
                  className="flex-1 min-h-[44px] rounded-md border border-rose-300 bg-white text-rose-700 font-bold text-sm hover:bg-rose-50 active:bg-rose-100 active:scale-[0.98] transition-transform touch-manipulation select-none disabled:opacity-50"
                  style={{ WebkitTapHighlightColor: "rgba(190,18,60,0.2)" }}
                  data-testid={`m-lp-reject-${row.draft_id}`}>
            {isBusy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Reject"}
          </button>
          <button type="button"
                  onClick={() => onApprove(row)}
                  disabled={isBusy}
                  className="flex-1 min-h-[44px] rounded-md bg-cyan-700 text-white font-bold text-sm hover:bg-cyan-800 active:bg-cyan-900 active:scale-[0.98] transition-transform touch-manipulation select-none disabled:opacity-50"
                  style={{ WebkitTapHighlightColor: "rgba(6,182,212,0.4)" }}
                  data-testid={`m-lp-approve-${row.draft_id}`}>
            {isBusy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Approve"}
          </button>
        </div>
      )}
    </div>
  );
}

export function MobileApprovalsView({
  view, setView, rows, loading, canAct,
  selected, toggleOne, toggleAll, selectedCount, busy,
  onApprove, onReject, onBulk, onRefresh, hasHistory = true,
}) {
  const nav = useNavigate();
  const tabs = [
    { key: "awaiting_me",  label: "Awaiting me",  count: view === "awaiting_me" ? rows.length : null },
    { key: "submitted_by_me", label: "Submitted", count: view === "submitted_by_me" ? rows.length : null },
    ...(hasHistory ? [{ key: "history", label: "History", count: null }] : []),
  ];
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="m-lp-approvals-root">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-40 h-14 bg-cyan-800 text-white flex items-center px-2 shadow-md">
        <button type="button"
                onClick={() => nav("/")}
                className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-cyan-700 active:bg-cyan-900 touch-manipulation"
                data-testid="m-lp-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-bold tracking-tight flex-1 text-center pr-11">LP Approvals</h1>
        <button type="button"
                onClick={onRefresh}
                className="p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-cyan-700 active:bg-cyan-900 touch-manipulation"
                data-testid="m-lp-refresh">
          <RefreshCw className="w-5 h-5" />
        </button>
      </header>

      {/* Tab strip */}
      <nav className="sticky top-14 z-30 bg-white border-b border-slate-200 flex" data-testid="m-lp-tabs">
        {tabs.map((t) => (
          <button key={t.key}
                  type="button"
                  onClick={() => setView(t.key)}
                  className={`flex-1 min-h-[48px] text-sm font-bold tracking-wide touch-manipulation select-none active:bg-slate-100 transition-colors ${
                    view === t.key
                      ? "text-cyan-800 border-b-2 border-cyan-700"
                      : "text-slate-500 border-b-2 border-transparent"
                  }`}
                  data-testid={`m-lp-tab-${t.key}`}>
            {t.label}
            {t.count != null && (
              <span className="ml-1.5 text-xs text-slate-400 font-normal">({t.count})</span>
            )}
          </button>
        ))}
      </nav>

      {/* Card list */}
      <main className="flex-1 p-3 pb-24 space-y-3 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-center py-16 text-slate-400" data-testid="m-lp-empty">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <div className="text-sm font-semibold">Nothing here</div>
            <div className="text-xs mt-1">All caught up. New drafts appear as they come.</div>
          </div>
        )}
        {!loading && rows.map((r) => (
          <MobileApprovalCard key={r.draft_id}
                                row={r} canAct={canAct}
                                selected={selected.has(r.draft_id)}
                                onToggle={toggleOne}
                                busy={busy}
                                onApprove={onApprove}
                                onReject={onReject} />
        ))}
      </main>

      {/* Sticky bottom bulk-action bar (only when selection) */}
      {selectedCount > 0 && canAct && (
        <div className="sticky bottom-0 z-40 h-16 bg-white border-t border-slate-200 shadow-[0_-2px_8px_rgba(0,0,0,0.08)] flex items-center px-3 gap-3"
             data-testid="m-lp-bulk-bar">
          <div className="flex-1 text-sm font-bold text-slate-800">
            {selectedCount} selected
          </div>
          <button type="button"
                  onClick={() => onBulk("reject")}
                  className="min-h-[48px] px-4 rounded-md border border-rose-300 bg-white text-rose-700 font-bold text-sm active:scale-[0.98] transition-transform touch-manipulation"
                  data-testid="m-lp-bulk-reject">
            Reject
          </button>
          <button type="button"
                  onClick={() => onBulk("approve")}
                  className="min-h-[48px] px-4 rounded-md bg-cyan-700 text-white font-bold text-sm active:scale-[0.98] transition-transform touch-manipulation"
                  data-testid="m-lp-bulk-approve">
            Approve All
          </button>
        </div>
      )}
    </div>
  );
}
